/**
 * Burmester — app entry point. Owns state, the render loop, and the bridge to
 * the synthesis worker; delegates drawing to CanvasView, pointer handling to
 * interaction.ts and the panel to ui.ts.
 */

import "./styles.css";
import { CanvasView, LIGHT, DARK } from "./canvas.js";
import { attachInteractions } from "./interaction.js";
import { buildUI, themeIcon } from "./ui.js";
import {
  createInitialState,
  QUALITY,
  type State,
  type ThemeChoice,
} from "./state.js";
import { MECHANISM_PRESETS, TARGET_PRESETS } from "./presets.js";
import { solvePosition } from "../kinematics/solve.js";
import { traceCoupler } from "../kinematics/trace.js";
import { cloneSpec, type MechanismSpec } from "../kinematics/types.js";
import type { WorkerRequest, WorkerResponse } from "./protocol.js";
import { startSynthesis as startSynthesisRun, type SynthHandle } from "./runner.js";

type P2 = [number, number];

const REPO_URL = "https://github.com/EivinDDu/burmester";

const app = document.getElementById("app")!;
app.innerHTML = `
  <header>
    <div class="brand">
      <strong>Burmester</strong>
      <span>differentiable linkage synthesizer</span>
    </div>
    <div class="head-actions">
      <a href="${REPO_URL}" target="_blank" rel="noopener">GitHub</a>
      <button data-theme-toggle title="Theme"></button>
    </div>
  </header>
  <main>
    <canvas id="stage"></canvas>
    <div class="hint" data-hint></div>
  </main>
  <aside id="panel"></aside>
`;

const canvas = document.getElementById("stage") as HTMLCanvasElement;
const view = new CanvasView(canvas);
const state: State = createInitialState();

// ---------------------------------------------------------------------------
// theme
// ---------------------------------------------------------------------------

const prefersDark = window.matchMedia("(prefers-color-scheme: dark)");
function resolvedTheme(t: ThemeChoice): "light" | "dark" {
  return t === "auto" ? (prefersDark.matches ? "dark" : "light") : t;
}
function applyTheme(): void {
  const r = resolvedTheme(state.theme);
  document.documentElement.dataset.theme = r;
  view.palette = r === "dark" ? DARK : LIGHT;
  const btn = document.querySelector("[data-theme-toggle]") as HTMLButtonElement;
  if (btn) btn.textContent = themeIcon(state.theme);
  requestRender();
}
prefersDark.addEventListener("change", () => {
  if (state.theme === "auto") applyTheme();
});
document.querySelector("[data-theme-toggle]")!.addEventListener("click", () => {
  const order: ThemeChoice[] = ["auto", "light", "dark"];
  state.theme = order[(order.indexOf(state.theme) + 1) % order.length];
  applyTheme();
});

// ---------------------------------------------------------------------------
// state plumbing
// ---------------------------------------------------------------------------

let renderQueued = false;
function requestRender(): void {
  if (renderQueued) return;
  renderQueued = true;
  requestAnimationFrame(frame);
}

function setSpec(spec: MechanismSpec): void {
  state.spec = spec;
  state.specVersion++;
  state.trace = null;
  refreshPanel();
  requestRender();
}
function mutateSpec(fn: (s: MechanismSpec) => void): void {
  const next = cloneSpec(state.spec);
  fn(next);
  setSpec(next);
}
function mutateState(fn: (s: State) => void): void {
  fn(state);
  applyDerived();
  refreshPanel();
  requestRender();
}

function applyDerived(): void {
  // topology change without an explicit preset: swap to that topology's default
  if (state.spec.topology !== state.topology) {
    const preset = MECHANISM_PRESETS.find((p) => p.topology === state.topology);
    if (preset) {
      state.spec = preset.make();
      state.specVersion++;
      state.trace = null;
    }
  }
}

function setTarget(pts: P2[] | null): void {
  state.target = pts;
  state.result = null;
  state.progress = null;
  refreshPanel();
  requestRender();
}

function loadTargetPreset(id: string): void {
  const p = TARGET_PRESETS.find((x) => x.id === id);
  if (!p) return;
  const pts = p.make();
  // place the preset roughly where the current mechanism's curve sits
  const trace = ensureTrace();
  if (trace && trace.length) {
    const c = centroid(trace);
    for (const q of pts) {
      q[0] += c[0];
      q[1] += c[1];
    }
  }
  setTarget(pts);
  view.fit([...pts, ...state.spec.grounds], 0.4);
}

function loadMechanismPreset(id: string): void {
  const p = MECHANISM_PRESETS.find((x) => x.id === id);
  if (!p) return;
  state.topology = p.topology;
  setSpec(p.make());
  view.fit([...(ensureTrace() ?? []), ...state.spec.grounds], 0.35);
}

function clearTarget(): void {
  setTarget(null);
}
function enterDrawMode(): void {
  mutateState((s) => {
    s.mode = s.mode === "draw" ? "idle" : "draw";
  });
}

function centroid(pts: P2[]): P2 {
  let x = 0;
  let y = 0;
  for (const p of pts) {
    x += p[0];
    y += p[1];
  }
  return [x / pts.length, y / pts.length];
}

// ---------------------------------------------------------------------------
// trace cache + pose
// ---------------------------------------------------------------------------

function ensureTrace(): P2[] | null {
  if (state.trace) return state.trace;
  const tr = traceCoupler(state.spec, { samples: 220 });
  // drop samples that failed to assemble so branch-switch garbage isn't drawn
  state.trace = tr.curve.filter((_, i) => tr.converged[i]);
  state.traceOk = tr.ok;
  state.convergedFraction = tr.convergedFraction;
  return state.trace;
}

function computePose(): void {
  const spec = state.spec;
  const seed = spec.free.flatMap((f) => f.guess);
  const sol = solvePosition(spec, state.driverAngle, seed, { maxIterations: 60 });
  state.pose = {
    positions: sol.positions as P2[],
    coupler: sol.coupler,
    converged: sol.converged,
  };
}

// ---------------------------------------------------------------------------
// synthesis (worker, or main-thread fallback)
// ---------------------------------------------------------------------------

let synthHandle: SynthHandle | null = null;

function startSynthesis(): void {
  if (!state.target || state.synthRunning) return;
  synthHandle?.cancel();
  const q = QUALITY[state.quality];
  const req: Extract<WorkerRequest, { type: "synthesize" }> = {
    type: "synthesize",
    target: state.target,
    topology: state.topology,
    seed: state.seed,
    restarts: q.restarts,
    itersPerRestart: q.itersPerRestart,
    polishIters: q.polishIters,
    samples: q.samples,
    refineSamples: q.refineSamples,
    allowScale: state.allowScale,
  };
  state.synthRunning = true;
  state.progress = null;
  state.result = null;
  state.mode = "synthesizing";
  refreshPanel();
  synthHandle = startSynthesisRun(req, { onMessage: onWorkerMessage });
}

function stopSynthesis(): void {
  synthHandle?.cancel();
}

function onWorkerMessage(msg: WorkerResponse): void {
  if (msg.type === "progress") {
    state.progress = msg;
    // live-preview the best mechanism so far
    state.spec = msg.spec;
    state.specVersion++;
    state.trace = msg.curve.length ? msg.curve : null;
    refreshPanel();
    requestRender();
  } else if (msg.type === "done") {
    state.synthRunning = false;
    state.mode = "idle";
    state.result = msg.result;
    state.progress = null;
    state.spec = msg.result.placedSpec;
    state.topology = msg.result.placedSpec.topology;
    state.specVersion++;
    state.trace = msg.result.curve;
    view.fit([...msg.result.curve, ...state.spec.grounds], 0.35);
    refreshPanel();
    requestRender();
    const rms = Math.sqrt(Math.max(msg.result.parts.chamfer, 0));
    const w = msg.result.curve.reduce(
      (m, p) => Math.max(m, Math.abs(p[0] - msg.result.curve[0][0])),
      1e-6,
    );
    setHint(
      `synthesis complete — coupler curve within ${((rms / w) * 100).toFixed(1)}% of the target` +
        (msg.result.grashof.crankRotates ? "" : " · note: input crank does not fully rotate"),
      6000,
    );
  } else if (msg.type === "error") {
    state.synthRunning = false;
    state.mode = "idle";
    setHint(`synthesis error: ${msg.message}`, 4000);
    refreshPanel();
  }
}

function acceptResult(): void {
  state.progress = null;
  refreshPanel();
  requestRender();
}

// ---------------------------------------------------------------------------
// hint line
// ---------------------------------------------------------------------------

let hintTimer = 0;
function setHint(text: string, ms = 0): void {
  const el = document.querySelector("[data-hint]") as HTMLElement | null;
  if (!el) return;
  el.textContent = text;
  el.classList.toggle("show", !!text);
  window.clearTimeout(hintTimer);
  hintStickyUntil = ms ? performance.now() + ms : 0;
  if (ms) hintTimer = window.setTimeout(() => setHint(""), ms);
}
let hintStickyUntil = 0;
/** Idle/status hints yield to a timed hint (e.g. "synthesis complete"). */
function setIdleHint(text: string): void {
  if (performance.now() < hintStickyUntil) return;
  setHint(text);
}

// ---------------------------------------------------------------------------
// render loop
// ---------------------------------------------------------------------------

let lastT = performance.now();
function frame(now: number): void {
  renderQueued = false;
  const dt = Math.min(0.05, (now - lastT) / 1000);
  lastT = now;

  if (state.playing && !state.synthRunning) {
    state.driverAngle = (state.driverAngle + state.speed * dt) % (2 * Math.PI);
  }
  ensureTrace();
  computePose();

  view.render({
    spec: state.spec,
    driverAngle: state.driverAngle,
    trace: state.trace,
    target: state.target,
    ghostTrace: null,
    drawingPath: state.mode === "draw" ? state.target : null,
    showLabels: state.showLabels,
  });

  if (state.mode === "draw") setIdleHint("draw a path on the canvas — release to set it as the target");
  else if (!state.target) setIdleHint("draw a target curve, or pick one from the gallery →");
  else if (state.synthRunning) setIdleHint("synthesizing… hit Stop to keep the best so far");
  else setIdleHint("");

  if (state.playing || state.synthRunning) requestRender();
}

// ---------------------------------------------------------------------------
// boot
// ---------------------------------------------------------------------------

let refreshPanel: () => void = () => {};
const updatePanel = buildUI(document.getElementById("panel")!, {
  state,
  view,
  mutateState,
  loadMechanismPreset,
  loadTargetPreset,
  clearTarget,
  enterDrawMode,
  startSynthesis,
  stopSynthesis,
  acceptResult,
  requestRender,
  applyTheme,
});
refreshPanel = () => updatePanel(state);

attachInteractions({
  view,
  state,
  mutateSpec,
  setTarget: (pts) => setTarget(pts as P2[] | null),
  setMode: (m) => mutateState((s) => (s.mode = m)),
  requestRender,
});

function onResize(): void {
  view.resize();
  requestRender();
}
window.addEventListener("resize", onResize);

view.resize();
applyTheme();
loadMechanismPreset("dwell");
view.fit([...(ensureTrace() ?? []), ...state.spec.grounds], 0.4);
refreshPanel();
requestRender();
